import { useCallback } from "react";
import { useErrorBoundary } from "react-error-boundary";
import type { FetchResult } from "@apollo/client";
import { useMutation } from "@apollo/client";
import type { OperationVariables } from "@apollo/client/core";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { unflatten } from "flat";
import type { Error } from "gql";
import type { DocumentNode } from "graphql";
import { isNil } from "ramda";

export const useDryRunMutation = <
  TFormData extends Record<string, unknown>,
  TData = unknown,
  TVariables = OperationVariables,
>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: {
    varSetter: (values: TFormData) => TVariables;
    dataGetter: (
      response?: FetchResult<TData>
    ) => (TData[keyof TData] & { errors?: Array<Maybe<Error>> | undefined | null }) | undefined | null;
    parseError?: (error: Error) => Error;
  }
): ((values: TFormData) => Promise<ValidationErrors>) => {
  const { varSetter, dataGetter, parseError } = options;
  const { showBoundary: onError } = useErrorBoundary();
  const [executeDryRun] = useMutation(mutation, { onError });
  return useCallback(
    (values: TFormData): Promise<ValidationErrors> => {
      const variables = varSetter(values);
      return executeDryRun({ variables: { ...variables, dryRun: true } })
        .then<ValidationErrors>((qr) => {
          const validationErrors = dataGetter(qr)?.errors;
          if (!isNil(validationErrors)) {
            return unflatten(
              Object.fromEntries(
                validationErrors
                  .filter(
                    (error: Maybe<Error>) =>
                      !isNil(error) && Boolean(error) && Boolean(error.path) && Boolean(error.message)
                  )
                  .map((error: Maybe<Error>) => (parseError ? parseError(error as Error) : error))
                  .map((error: Maybe<Error>) => [(error as Error).path, (error as Error).message])
              )
            );
          }
          return undefined;
        })
        .catch((error) => {
          onError(error);
        });
    },
    [dataGetter, executeDryRun, onError, parseError, varSetter]
  );
};
